import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase/client'
import { toast } from 'sonner'

interface AreaN1 {
  id: string
  nome: string
  pipefy_database_id: string
}

interface GestorContextType {
  areas: AreaN1[]
  anos: number[]
  areaSelecionada: string
  setAreaSelecionada: (area: string) => void
  /** `pipefy_database_id` do centro N1 selecionado (para filtrar verbas adicionais etc.) */
  areaCentroPipefyId: string | null
  anoSelecionado: string
  setAnoSelecionado: (ano: string) => void
  loading: boolean
}

const GestorContext = createContext<GestorContextType | undefined>(undefined)

export function GestorProvider({ children }: { children: React.ReactNode }) {
  const [areas, setAreas] = useState<AreaN1[]>([])
  const [anos, setAnos] = useState<number[]>([])
  const [areaSelecionada, setAreaSelecionada] = useState<string>('')
  const [anoSelecionado, setAnoSelecionado] = useState<string>('')
  const [loading, setLoading] = useState(true)

  const fetchInitialData = useCallback(async () => {
    setLoading(true)
    try {
      // Fetch Areas N1
      const { data: areasData, error: areasError } = await supabase
        .from('solis_centros_custo')
        .select('id, nome, pipefy_database_id')
        .eq('nivel', 'Nível 1')
        .order('nome')

      if (areasError) throw areasError

      if (areasData && areasData.length > 0) {
        setAreas(areasData)
        setAreaSelecionada(areasData[0].nome)
      }

      // Fetch Anos
      const { data: anosData, error: anosError } = await supabase
        .from('solis_vw_orcamento_por_tipo_centro')
        .select('ano')
        .order('ano', { ascending: false })

      if (anosError) throw anosError

      const anosUnicos = [...new Set(anosData?.map((d: any) => d.ano) || [])].sort((a, b) => b - a) as number[]
      setAnos(anosUnicos)
      if (anosUnicos.length > 0) {
        setAnoSelecionado(String(anosUnicos[0]))
      }
    } catch (error) {
      console.error('Erro ao carregar dados iniciais do gestor:', error)
      toast.error('Erro ao carregar filtros.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchInitialData()
  }, [fetchInitialData])

  const areaCentroPipefyId =
    areas.find((a) => a.nome === areaSelecionada)?.pipefy_database_id ?? null

  return (
    <GestorContext.Provider
      value={{
        areas,
        anos,
        areaSelecionada,
        setAreaSelecionada,
        areaCentroPipefyId,
        anoSelecionado,
        setAnoSelecionado,
        loading
      }}
    >
      {children}
    </GestorContext.Provider>
  )
}

export function useGestor() {
  const context = useContext(GestorContext)
  if (context === undefined) {
    throw new Error('useGestor must be used within a GestorProvider')
  }
  return context
}
